/**
 * UTM parameter capture and persistence.
 *
 * Captures UTM params from the URL on first page load, persists them
 * to localStorage so they survive navigation, and exposes helpers to
 * set them as Amplitude user properties (setOnce for first-touch attribution).
 *
 * Stored UTMs are the attribution of record: they feed Stripe checkout
 * metadata, the purchase event and the Mongo lead profile. They are first
 * touch and are never overwritten. The newest campaign is tracked separately
 * via getLastTouchUTMs.
 */
import * as amplitude from '@amplitude/analytics-browser';
import { extractReferralCodeFromLocation } from './referralLanding';

const UTM_KEYS = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_content',
    'utm_term',
] as const;

export type UTMParams = Partial<Record<typeof UTM_KEYS[number], string>>;

const CLICK_ID_KEYS = ['ttclid', 'fbclid', 'gclid'] as const;
type ClickIdKey = typeof CLICK_ID_KEYS[number];

/** AppLovin ad click IDs — must survive SPA navigation for attribution. */
const APPLOVIN_CLICK_ID_KEYS = ['aleid', 'alart'] as const;
type AppLovinClickIdKey = typeof APPLOVIN_CLICK_ID_KEYS[number];

const CLICK_IDS_STORAGE_KEY = 'mc_click_ids';
const APPLOVIN_CLICK_IDS_STORAGE_KEY = 'mc_applovin_click_ids';
const LAST_TOUCH_STORAGE_KEY = 'mc_last_touch_utms';

/**
 * Read click IDs (ttclid, fbclid, gclid) from the current URL and persist
 * them to localStorage. Only writes a key if present in the URL — existing
 * stored values survive navigations that don't carry the param.
 */
export function captureClickIds(): Record<ClickIdKey, string> {
    const existing = getStoredClickIds();
    if (typeof window === 'undefined') return existing;

    const params = new URLSearchParams(window.location.search);
    const merged: Record<ClickIdKey, string> = { ...existing };
    let changed = false;

    CLICK_ID_KEYS.forEach((key) => {
        const value = params.get(key);
        if (value) {
            merged[key] = value;
            changed = true;
        }
    });

    if (changed) {
        try {
            localStorage.setItem(CLICK_IDS_STORAGE_KEY, JSON.stringify(merged));
        } catch { /* storage disabled */ }
    }

    return merged;
}

/**
 * Read previously stored click IDs from localStorage. Returns empty
 * string for any missing key so callers can destructure safely.
 */
/**
 * Read AppLovin click IDs (aleid, alart) from the URL and persist to localStorage.
 * AppLovin appends these on ad clicks; dropping them breaks campaign attribution.
 */
export function captureAppLovinClickIds(): Record<AppLovinClickIdKey, string> {
    const existing = getStoredAppLovinClickIds();
    if (typeof window === 'undefined') return existing;

    const params = new URLSearchParams(window.location.search);
    const merged: Record<AppLovinClickIdKey, string> = { ...existing };
    let changed = false;

    APPLOVIN_CLICK_ID_KEYS.forEach((key) => {
        const value = params.get(key);
        if (value) {
            merged[key] = value;
            changed = true;
        }
    });

    if (changed) {
        try {
            localStorage.setItem(APPLOVIN_CLICK_IDS_STORAGE_KEY, JSON.stringify(merged));
        } catch { /* storage disabled */ }
    }

    return merged;
}

export function getStoredAppLovinClickIds(): Record<AppLovinClickIdKey, string> {
    const empty: Record<AppLovinClickIdKey, string> = { aleid: '', alart: '' };
    if (typeof window === 'undefined') return empty;

    try {
        const raw = localStorage.getItem(APPLOVIN_CLICK_IDS_STORAGE_KEY);
        if (!raw) return empty;
        const parsed = JSON.parse(raw) as Partial<Record<AppLovinClickIdKey, string>>;
        return {
            aleid: parsed.aleid || '',
            alart: parsed.alart || '',
        };
    } catch {
        return empty;
    }
}

export function getStoredClickIds(): Record<ClickIdKey, string> {
    const empty: Record<ClickIdKey, string> = {
        ttclid: '',
        fbclid: '',
        gclid: '',
    };
    if (typeof window === 'undefined') return empty;

    try {
        const raw = localStorage.getItem(CLICK_IDS_STORAGE_KEY);
        if (!raw) return empty;
        const parsed = JSON.parse(raw) as Partial<Record<ClickIdKey, string>>;
        return {
            ttclid: parsed.ttclid || '',
            fbclid: parsed.fbclid || '',
            gclid: parsed.gclid || '',
        };
    } catch {
        return empty;
    }
}

function readUtmsFromUrl(): UTMParams {
    const params = new URLSearchParams(window.location.search);
    const utms: UTMParams = {};
    UTM_KEYS.forEach((key) => {
        const value = params.get(key);
        if (value) utms[key] = value;
    });
    return utms;
}

/**
 * Read UTMs from the current URL, falling back to localStorage.
 *
 * First touch wins. A returning visitor who arrives from an owned channel —
 * an abandoned-cart email, a retargeting link — must not overwrite the paid
 * campaign that originally acquired them, or that campaign loses credit for
 * the sale in Stripe metadata and the purchase event.
 */
export function captureUTMs(): UTMParams {
    if (typeof window === 'undefined') return {};

    const urlUtms = readUtmsFromUrl();
    const stored = getStoredUTMs();

    if (Object.keys(urlUtms).length === 0) return stored;

    try {
        localStorage.setItem(LAST_TOUCH_STORAGE_KEY, JSON.stringify(urlUtms));
    } catch { /* storage disabled */ }

    // A UTM set is a single touch — writing key by key would blend two
    // campaigns whenever the newer link carries fewer params.
    if (Object.keys(stored).length > 0) return stored;

    try {
        UTM_KEYS.forEach((key) => {
            const value = urlUtms[key];
            if (value) localStorage.setItem(key, value);
        });
    } catch { /* storage disabled */ }

    return urlUtms;
}

/**
 * Most recent campaign the visitor arrived from. Kept apart from the
 * first-touch values so email/retargeting clicks stay measurable without
 * rewriting acquisition attribution.
 */
export function getLastTouchUTMs(): UTMParams {
    if (typeof window === 'undefined') return {};

    try {
        const raw = localStorage.getItem(LAST_TOUCH_STORAGE_KEY);
        if (!raw) return {};
        const parsed = JSON.parse(raw) as UTMParams;
        const utms: UTMParams = {};
        UTM_KEYS.forEach((key) => {
            if (parsed[key]) utms[key] = parsed[key];
        });
        return utms;
    } catch {
        return {};
    }
}

/**
 * Read previously stored UTMs from localStorage (no URL parsing).
 */
export function getStoredUTMs(): UTMParams {
    if (typeof window === 'undefined') return {};

    const utms: UTMParams = {};
    UTM_KEYS.forEach((key) => {
        const stored = localStorage.getItem(key);
        if (stored) utms[key] = stored;
    });
    return utms;
}

/** First-touch UTMs for Mongo profile update at lead capture (Meta campaign attribution). */
export function appendLeadCaptureUtmsToProfile(profileData: Record<string, unknown>): void {
    const utms = getStoredUTMs();
    if (utms.utm_source) profileData.utm_source = utms.utm_source;
    if (utms.utm_campaign) profileData.utm_campaign = utms.utm_campaign;
    if (utms.utm_content) profileData.utm_content = utms.utm_content;
}

/**
 * Persist ?ref= / ?referralCode= / /join/:code on first landing so creator
 * links survive the quiz before EmailCapture mounts.
 *
 * Creator share links only carry ?ref= (no UTMs). Mirror the code into
 * first-touch utm_content so Stripe/user attribution still resolves by
 * referral code.
 */
export function captureReferralCodeFromUrl(): string | null {
    if (typeof window === 'undefined') {
        try {
            return sessionStorage.getItem('referralCode');
        } catch {
            return null;
        }
    }

    const fromUrl = extractReferralCodeFromLocation(window.location.search, window.location.pathname);
    if (fromUrl?.trim()) {
        const raw = fromUrl.trim();
        const code = raw.toUpperCase();
        try {
            sessionStorage.setItem('referralCode', code);
        } catch { /* storage disabled */ }
        try {
            if (!localStorage.getItem('utm_content')) {
                localStorage.setItem('utm_content', raw);
            }
        } catch { /* storage disabled */ }
        return code;
    }
    try {
        return sessionStorage.getItem('referralCode');
    } catch {
        return null;
    }
}

/**
 * Set captured UTMs as Amplitude user properties: utm_* with setOnce
 * (first-touch acquisition) and last_utm_* with set (newest campaign, so
 * email and retargeting clicks stay segmentable).
 */
export function setUTMUserProperties(): void {
    const utms = captureUTMs();
    const lastTouch = getLastTouchUTMs();
    if (Object.keys(utms).length === 0 && Object.keys(lastTouch).length === 0) return;

    const identify = new amplitude.Identify();
    UTM_KEYS.forEach((key) => {
        if (utms[key]) {
            identify.setOnce(key, utms[key]!);
        }
        if (lastTouch[key]) {
            identify.set(`last_${key}`, lastTouch[key]!);
        }
    });
    amplitude.identify(identify);
}
